// Shared form-schema types + parser.
//
// Originally seeded by the deal flow for the Criteria Checklist
// popup forms (~30 structured items). External Intake & Collection v1.0
// PR 2 extends the schema with the full set required by the spec §4.1
// template builder: number / percentage / email / phone / ssn-ein,
// radio / yesno, address / fileattach / signature / initials / datesigned,
// section heading / instruction / page break, and repeat groups. Per-field
// behaviors (defaultValue, prefillFrom, validation, helpText, visibleWhen)
// flow through the parser too.
//
// Backward-compat contract: the legacy CriteriaChecklist renderer
// (PopupFormFromSchema) only handles the original 7 types and renders
// `null` for anything else, so extending the type union does not break
// existing seeded schemas. Unknown types still degrade to textarea.

export type FormFieldType =
  // Original 7 (Criteria Checklist)
  | "text"
  | "textarea"
  | "currency"
  | "date"
  | "dropdown"
  | "checkbox"
  | "multi-select"
  // Extended set (Intake & Collection v1.0 PR 2, spec §4.1)
  | "number"
  | "percentage"
  | "email"
  | "phone"
  | "ssn"
  | "ein"
  | "radio"
  | "yesno"
  | "address"
  | "fileattach"
  | "signature"
  | "initials"
  | "datesigned"
  // Structural (Intake & Collection v1.0 PR 2, spec §4.1)
  | "sectionheading"
  | "instruction"
  | "pagebreak"
  | "repeatgroup";

/** Sender-Fields v1 — who fills a field. Default (absent) = "Recipient". */
export type FilledBy = "Sender" | "Recipient";

/** Sender-Fields v1 — for a Sender field, where its value originates. */
export type SenderValueSource = "Manual" | "DealField" | "Computed" | "System";

/**
 * Conditional-visibility expression. The renderer hides the field when
 * `equals` does not match the dependency field's current value, or when
 * `in` is provided and the value is not in the list. Other forms are
 * reserved for later; the validator + parser accept the two shapes the
 * builder produces today.
 */
export interface VisibleWhen {
  field: string;
  equals?: string | number | boolean | null;
  in?: ReadonlyArray<string | number | boolean>;
}

/**
 * Per-field validation hints. The renderer in PR 4 applies them; the
 * builder authors them. Server-side validation in PR 2 is intentionally
 * shape-only — the regex/min/max are advisory metadata, not enforced at
 * save time.
 */
export interface FormFieldValidation {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  /** ECMAScript regex source string. */
  pattern?: string;
}

export interface FormField {
  /** Key under which the value is stored in the response JSON. Always
   *  present — structural fields (sectionheading / instruction /
   *  pagebreak) get a synthetic name from the builder so the renderer
   *  can use it as a stable React key. */
  name: string;
  /** Display label. */
  label: string;
  type: FormFieldType;
  required?: boolean;
  /** For dropdown / radio / multi-select. */
  options?: ReadonlyArray<{ value: string; label: string }>;
  helpText?: string;
  defaultValue?: string | number | boolean | null;
  /** Token referring to a known owner/recipient field (e.g.
   *  "recipient.fullName", "owner.name"). The renderer in PR 4 resolves
   *  it from the package context; the builder writes it. */
  prefillFrom?: string;
  /** Deal Submission spec §5.1 — outbound import target. Picked
   *  from the server-driven mapping-token catalog in the Deal Submission
   *  template builder. The import engine copies the field's
   *  answer into the column / create-request field this token resolves
   *  to. Distinct from `prefillFrom` (inbound default). */
  mapsTo?: string;
  /** Sender-Fields v1 — who fills this field. "Recipient" (default) is the
   *  external party (existing behaviour); "Sender" is our staff at send time
   *  (or auto-resolved from a record). Sender fields render read-only to the
   *  recipient and never block their submit. */
  filledBy?: FilledBy;
  /** Sender-Fields v1 — for a Sender field, where its value comes from. */
  valueSource?: SenderValueSource;
  /** Sender-Fields v1 — for a DealField sender field, the deal/addendum
   *  property token (e.g. "deal.purchasePrice", "addendum.loanAmount"). */
  sourceToken?: string;
  /** Letter rendering — a percentage field that declares it prints exactly this
   *  many decimal places ("2.00%"); read by the server's CollectionLetterRenderer.
   *  Carried here so an admin edit through the builder keeps it. */
  decimals?: number;
  /** Owner Recapitalization Loan (ruling Q10) — a checkbox that cannot be
   *  submitted unchecked (recap-only; seeded on two fields). Unlike `required`,
   *  which a checkbox meets even when unchecked, this needs the value `true`.
   *  Top-level checkboxes only; enforced by the portal
   *  (`findFirstMissingRequired`) and the server
   *  (`Core/Common/CollectionMustBeChecked`). */
  mustBeChecked?: boolean;
  validation?: FormFieldValidation;
  visibleWhen?: VisibleWhen;
  /** Static text for structural elements (instruction / sectionheading). */
  text?: string;
  /** Nested fields for repeat groups. One level of nesting only. */
  fields?: FormField[];
  /** Label on the repeat-group "add another" button. */
  addAnotherLabel?: string;
}

export interface FormSchema {
  fields: FormField[];
  /** Optional version string — recorded for forward-compat. */
  version?: string;
}

/** Whether the type carries a stored value (so `name` is required) or is
 *  purely structural (heading / instruction / pagebreak). */
function isStructural(type: FormFieldType): boolean {
  return type === "sectionheading" || type === "instruction" || type === "pagebreak";
}

const KNOWN_TYPES: ReadonlySet<FormFieldType> = new Set<FormFieldType>([
  "text", "textarea", "currency", "date", "dropdown", "checkbox", "multi-select",
  "number", "percentage", "email", "phone", "ssn", "ein",
  "radio", "yesno", "address", "fileattach", "signature", "initials", "datesigned",
  "sectionheading", "instruction", "pagebreak", "repeatgroup",
]);

const CHOICE_TYPES: ReadonlySet<FormFieldType> = new Set<FormFieldType>([
  "dropdown", "radio", "multi-select",
]);

function parseField(raw: unknown, index: number): FormField | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;

  const typeRaw = typeof obj.type === "string" ? obj.type : "textarea";
  const type: FormFieldType = KNOWN_TYPES.has(typeRaw as FormFieldType)
    ? (typeRaw as FormFieldType)
    : "textarea";

  const explicitName = typeof obj.name === "string" ? obj.name : undefined;
  const label = typeof obj.label === "string" ? obj.label : (explicitName ?? "");

  // Structural fields don't need a stored value; synthesize a stable
  // key. Data fields require an explicit name (the parser drops them
  // otherwise — that contract is unchanged from the original parser).
  let name: string;
  if (isStructural(type)) {
    name = explicitName ?? `__struct_${type}_${index}`;
  } else {
    if (!explicitName) return null;
    if (!label) return null;
    name = explicitName;
  }

  const options = CHOICE_TYPES.has(type)
    ? Array.isArray(obj.options)
      ? (obj.options as unknown[]).flatMap((o) => {
          if (o && typeof o === "object") {
            const v = (o as Record<string, unknown>).value;
            const l = (o as Record<string, unknown>).label;
            if (typeof v === "string" && typeof l === "string") {
              return [{ value: v, label: l }];
            }
          }
          return [];
        })
      : []
    : undefined;

  const visibleWhenRaw = obj.visibleWhen;
  let visibleWhen: VisibleWhen | undefined;
  if (visibleWhenRaw && typeof visibleWhenRaw === "object") {
    const vw = visibleWhenRaw as Record<string, unknown>;
    const depField = typeof vw.field === "string" ? vw.field : null;
    if (depField) {
      const equalsHas = "equals" in vw;
      const inHas = Array.isArray(vw.in);
      if (equalsHas) {
        const v = vw.equals;
        if (v === null || ["string", "number", "boolean"].includes(typeof v)) {
          visibleWhen = { field: depField, equals: v as VisibleWhen["equals"] };
        }
      } else if (inHas) {
        visibleWhen = {
          field: depField,
          in: (vw.in as unknown[]).filter(
            (x) => ["string", "number", "boolean"].includes(typeof x),
          ) as ReadonlyArray<string | number | boolean>,
        };
      }
    }
  }

  const validation = parseValidation(obj.validation);

  const nested = type === "repeatgroup" && Array.isArray(obj.fields)
    ? (obj.fields as unknown[])
        .map((sub, j) => parseField(sub, j))
        .filter((f): f is FormField => f !== null)
    : undefined;

  const defaultValue = obj.defaultValue;
  const defaultValueOk =
    defaultValue === null
    || ["string", "number", "boolean"].includes(typeof defaultValue);

  return {
    name,
    label: label || (name ?? ""),
    type,
    required: !!obj.required,
    options,
    helpText: typeof obj.helpText === "string" ? obj.helpText : undefined,
    defaultValue: defaultValueOk ? (defaultValue as FormField["defaultValue"]) : undefined,
    prefillFrom: typeof obj.prefillFrom === "string" ? obj.prefillFrom : undefined,
    mapsTo: typeof obj.mapsTo === "string" ? obj.mapsTo : undefined,
    // Sender-Fields v1 — preserve through the parser (whitelist-rebuilt
    // objects drop anything not explicitly carried here).
    filledBy: obj.filledBy === "Sender" || obj.filledBy === "Recipient" ? obj.filledBy : undefined,
    valueSource:
      obj.valueSource === "Manual" || obj.valueSource === "DealField"
        || obj.valueSource === "Computed" || obj.valueSource === "System"
        ? obj.valueSource
        : undefined,
    sourceToken: typeof obj.sourceToken === "string" ? obj.sourceToken : undefined,
    decimals:
      typeof obj.decimals === "number" && Number.isInteger(obj.decimals) ? obj.decimals : undefined,
    mustBeChecked: type === "checkbox" && obj.mustBeChecked === true ? true : undefined,
    validation,
    visibleWhen,
    text: typeof obj.text === "string" ? obj.text : undefined,
    fields: nested,
    addAnotherLabel: typeof obj.addAnotherLabel === "string" ? obj.addAnotherLabel : undefined,
  };
}

function parseValidation(raw: unknown): FormFieldValidation | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const v = raw as Record<string, unknown>;
  const out: FormFieldValidation = {};
  if (typeof v.minLength === "number") out.minLength = v.minLength;
  if (typeof v.maxLength === "number") out.maxLength = v.maxLength;
  if (typeof v.min === "number") out.min = v.min;
  if (typeof v.max === "number") out.max = v.max;
  if (typeof v.pattern === "string") out.pattern = v.pattern;
  return Object.keys(out).length === 0 ? undefined : out;
}

/** The seeded criteria catalog schemas are JSON Schema draft-07 objects
 *  (`FormSchemaSeed.Default`), not `{fields: []}`. Each property becomes a
 *  field in property order: a boolean is a checkbox, anything else a
 *  textarea (the parser's fallback for an unknown type). `title` is the
 *  label, `description` the help text and the `required` array the
 *  required flags. */
function parseJsonSchemaProperties(
  properties: Record<string, unknown>,
  required: unknown,
): FormField[] {
  const requiredNames = Array.isArray(required)
    ? required.filter((r): r is string => typeof r === "string")
    : [];
  return Object.entries(properties).flatMap(([name, raw]) => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
    const prop = raw as Record<string, unknown>;
    const title = typeof prop.title === "string" && prop.title.trim() ? prop.title : name;
    const defaultValue = prop.default;
    const field: FormField = {
      name,
      label: title,
      type: prop.type === "boolean" ? "checkbox" : "textarea",
      required: requiredNames.includes(name),
      helpText: typeof prop.description === "string" ? prop.description : undefined,
      defaultValue:
        defaultValue === null || ["string", "number", "boolean"].includes(typeof defaultValue)
          ? (defaultValue as FormField["defaultValue"])
          : undefined,
      validation: parseValidation(prop),
    };
    return [field];
  });
}

/** Parse the JSON blob stored on CriteriaCatalogItem.formSchema or
 *  CollectionTemplate.FormSchemaJson. Returns null when the blob is
 *  empty, malformed, or carries no fields. A blob with no `fields` key is
 *  read as a draft-07 object schema (`properties`). */
export function parseFormSchema(blob: string | null | undefined): FormSchema | null {
  if (!blob || !blob.trim()) return null;
  try {
    const parsed = JSON.parse(blob) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const fields = (parsed as { fields?: unknown }).fields;
    const properties = (parsed as { properties?: unknown }).properties;
    if (
      fields === undefined
      && properties
      && typeof properties === "object"
      && !Array.isArray(properties)
    ) {
      const fromProperties = parseJsonSchemaProperties(
        properties as Record<string, unknown>,
        (parsed as { required?: unknown }).required,
      );
      return fromProperties.length === 0 ? null : { fields: fromProperties };
    }
    if (!Array.isArray(fields) || fields.length === 0) return null;
    const sanitized: FormField[] = [];
    fields.forEach((raw, i) => {
      const parsedField = parseField(raw, i);
      if (parsedField) sanitized.push(parsedField);
    });
    if (sanitized.length === 0) return null;
    return {
      fields: sanitized,
      version:
        typeof (parsed as { version?: unknown }).version === "string"
          ? ((parsed as { version: string }).version)
          : undefined,
    };
  } catch {
    return null;
  }
}

/** The Justification JSON itself — keys are FormField.name, values are
 *  primitives or string arrays (for multi-select). attachedDocumentId
 *  is a reserved key used by Attach Report to carry the document id
 *  alongside the form fields. */
export type JustificationData = Record<
  string,
  string | number | boolean | string[] | null
>;

export function parseJustification(blob: string | null | undefined): JustificationData {
  if (!blob || !blob.trim()) return {};
  try {
    const parsed = JSON.parse(blob) as unknown;
    if (parsed && typeof parsed === "object") {
      return parsed as JustificationData;
    }
    // Free-form text from a legacy textarea — wrap it.
    if (typeof parsed === "string") {
      return { __text: parsed };
    }
  } catch {
    // Existing items predating structured forms may have plain-text justifications.
    return { __text: blob };
  }
  return {};
}

export function stringifyJustification(data: JustificationData): string {
  return JSON.stringify(data);
}
